import React, { useRef } from 'react';
import * as Yup from 'yup';

import i18n from 'i18n-js';
import { Grid, Typography, Tooltip } from '@material-ui/core';
import { BaseModal } from 'components/UI';
import { Field, Formik } from 'formik';
import { TextField, MaterialSelectField } from 'components/FormFields';
import { useStyles, noUnderLine } from './Common.styled';
import HelpIcon from '@material-ui/icons/Help';
import { timeUnits } from 'constants/common';

export const VariableBufferModal = ({
  currentVariableBuffer,
  open,
  handleClick,
  handleAddBuffer,
  handleEditBuffer,
  error,
}) => {
  const formRef = useRef(null);
  const classes = useStyles();
  const noUnderlineTextField = noUnderLine();
  const initialValues = {
    time_range: {
      magnitude: 1,
      units: 'hr',
    },
    buffer: {
      magnitude: 1,
      units: 'hr',
    },
    is_default: false,
    ...currentVariableBuffer.buffer,
  };

  const handleSubmit = ({ ...values }) => {
    if (currentVariableBuffer.buffer) {
      handleEditBuffer(currentVariableBuffer.currentIndex, values);
    } else {
      handleAddBuffer(values);
    }
  };

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const formSchema = Yup.object().shape({
    time_range: Yup.object()
      .nullable()
      .when('is_default', {
        is: (val) => !Boolean(val),
        then: Yup.object().shape({
          magnitude: Yup.number()
            .integer(i18n.t('common.type_error_integer'))
            .min(1, i18n.t('common.custom_min_value', { min_value: 1 }))
            .required(i18n.t('common.required_field'))
            .typeError(i18n.t('common.type_error')),
          units: Yup.string().required(i18n.t('common.required_field')),
        }),
        otherwise: Yup.object().nullable().notRequired(),
      }),
    buffer: Yup.object().shape({
      magnitude: Yup.number()
        .integer(i18n.t('common.type_error_integer'))
        .min(1, i18n.t('common.custom_min_value', { min_value: 1 }))
        .required(i18n.t('common.required_field'))
        .typeError(i18n.t('common.type_error')),
      units: Yup.string().required(i18n.t('common.required_field')),
    }),
  });

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      buttonsVariant="outlined"
      title={
        currentVariableBuffer.buffer
          ? i18n.t('reservation_rulesets.edit_maintenance_buffer')
          : i18n.t('reservation_rulesets.add_maintenance_buffer')
      }
      enterButtonLabel={i18n.t('common.save')}
      enterButtonProps={{ 'data-cy': 'save-variable-buffer' }}
      maxWidth="460px"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        enableReinitialize={true}
        innerRef={formRef}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        <Grid container spacing={5} direction="column" style={{ marginBottom: 10 }}>
          <Grid item xs={12} direction="row">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h3" className={classes.modalInput}>
                      {i18n.t('reservation_rulesets.time_frame')}
                    </Typography>
                  </Grid>

                  {initialValues.is_default ? (
                    <Grid item xs={12}>
                      <span style={{ height: 20 }}>
                        {i18n.t('reservation_rulesets.time_range_fmt_default')}
                        <Tooltip
                          title={i18n.t('reservation_rulesets.time_range_fmt_default_tooltip')}
                          placement="top"
                          aria-label="help"
                        >
                          <HelpIcon color="primary" className={classes.helpIcon} />
                        </Tooltip>
                      </span>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={6}>
                        <Field
                          margin="none"
                          label={i18n.t('common.up_to')}
                          name={`time_range.magnitude`}
                          component={TextField}
                          inputProps={{ className: classes.input }}
                          required
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          margin="none"
                          options={timeUnits}
                          placeholder={i18n.t('common.select_type')}
                          name={`time_range.units`}
                          component={MaterialSelectField}
                          required
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <span className={classes.warning}>{error && error}</span>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h3" className={classes.modalInput}>
                      {i18n.t('reservation_rulesets.buffer')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      data-cy="buffer-magnitude-input"
                      margin="none"
                      label={i18n.t('common.number')}
                      name={`buffer.magnitude`}
                      component={TextField}
                      inputProps={{ className: classes.input }}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      data-cy="buffer-units-input"
                      margin="none"
                      options={timeUnits}
                      placeholder={i18n.t('common.select_type')}
                      name={`buffer.units`}
                      component={MaterialSelectField}
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Formik>
    </BaseModal>
  );
};
